import { queryOptions, useMutation } from "@tanstack/react-query";

import { env } from "@/env";
import {
  http,
  type RequestOptions,
  type HttpResponse,
} from "@/shared/api/client";

type Account = {
  id: number;
  login: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  activated: boolean;
  langKey: string;
  authorities: string[];
};

type PasswordChange = {
  oldPassword: string;
  newPassword: string;
};

type AccountRegistration = {
  email: string;
  password: string;
};

const accountKeys = {
  all: ["gateway", "account"] as const,
  detail: () => [...accountKeys.all, "detail"] as const,
};

const accountQuery = () =>
  queryOptions({
    queryKey: accountKeys.detail(),
    queryFn: async ({ signal }) => {
      const response = await fetchAccount({ signal });
      return response.data;
    },
  });

const useSaveAccountMutation = () =>
  useMutation({
    mutationFn: (account: Account) => saveAccount(account),
    meta: { errorMessageId: "Profile.saveError" },
  });

const useChangePasswordMutation = () =>
  useMutation({
    mutationFn: (passwords: PasswordChange) => changePassword(passwords),
    meta: {
      errorMessageId: "PasswordChange.changeError",
      skipInvalidation: true,
    },
  });

const useCheckEmailExistsMutation = () =>
  useMutation({
    mutationFn: (email: string) => checkEmailExists(email),
    meta: {
      errorMessageId: "validation.submitError",
      skipInvalidation: true,
    },
  });

// silent: the signup form parses the HttpError to pick between several specific messages
const useRegisterAccountMutation = () =>
  useMutation({
    mutationFn: (registration: AccountRegistration) =>
      registerAccount(registration),
    meta: { silent: true, skipInvalidation: true },
  });

const fetchAccount = (
  config?: RequestOptions,
): Promise<HttpResponse<Account>> =>
  http.get<Account>(accountsUrl(), { ...config, skipFarmId: true });

const saveAccount = (account: Account): Promise<HttpResponse<Account>> =>
  http.post<Account>(accountsUrl(), account, { skipFarmId: true });

const changePassword = (
  passwords: PasswordChange,
): Promise<HttpResponse<void>> =>
  http.post<void>(accountsUrl("/change-password"), passwords, {
    skipFarmId: true,
  });

// cf-gateway 401s GET /accounts?email= as soon as any other query param is
// present, so the global `language` default has to be dropped for this one call
// — with it the 401 trips the refresh interceptor and boots the user to /login.
const checkEmailExists = async (email: string): Promise<boolean> => {
  const response = await http.get<boolean>(accountsUrl(), {
    params: { email, language: undefined },
    skipFarmId: true,
  });
  return response.data;
};

const registerAccount = (
  registration: AccountRegistration,
): Promise<HttpResponse<Account>> =>
  http.post<Account>(accountsUrl("/register"), registration, {
    skipFarmId: true,
  });

const accountsUrl = (path = ""): string => `${env.GATEWAY_URL}/accounts${path}`;

export {
  accountKeys,
  accountQuery,
  useSaveAccountMutation,
  useChangePasswordMutation,
  useCheckEmailExistsMutation,
  useRegisterAccountMutation,
  type Account,
  type AccountRegistration,
  type PasswordChange,
};
